import { Box, makeStyles, Typography } from '@material-ui/core'
import { MARKETING_CONTENT_STATUS } from '../../../utils/marketing'
import { AddOutlined } from '@material-ui/icons'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'

import {
	createMarketingContent,
	reorderMarketingContent
} from '../../../actions/facilities/marketingContent.actions'
import { gray } from '../../../utils/colors'
import { MainButton } from '../../Button/MainButton'
import StyledAlert from '../../Common/StyledAlert'
import { CATEGORY_CONFIG, isNewRecord } from './marketingContentConfig'
import MarketingContentItem from './MarketingContentItem'

// Non-active records sit out of the live list — mirrors the API's NO_ORDER.
const NO_ORDER = -1

const useStyles = makeStyles(() => ({
	wrapper: {
		marginBottom: 24,
		// Without this, button icons inherit a stray white fill from the theme and disappear.
		'& .MuiButton-root .MuiSvgIcon-root': {
			color: 'inherit',
			fill: 'currentColor'
		}
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBottom: 16
	},
	titleGroup: { display: 'flex', alignItems: 'center', gap: 16 },
	title: { fontWeight: 800, fontSize: '1.5rem' },
	empty: { padding: 24, textAlign: 'center', color: gray[700] }
}))

/** Active records in order, then inactive drafts — same order the API uses. */
const sortForDisplay = (list) => {
	const sorted = [...list]

	sorted.sort((a, b) => {
		if ((a.status === MARKETING_CONTENT_STATUS.ACTIVE) !== (b.status === MARKETING_CONTENT_STATUS.ACTIVE)) {
			return a.status === MARKETING_CONTENT_STATUS.ACTIVE ? -1 : 1
		}

		return (a.order || 0) - (b.order || 0)
	})

	return sorted
}

/**
 * Reindexes active records to 0..n-1, mirroring what the server does on activate / deactivate /
 * delete. Only the touched record comes back, so rather than refetch we reproduce the server
 * state locally: an activated record sorts last, a deactivated one drops to -1 and its gap closes.
 */
const renumber = (list) => {
	const result = []
	let next = 0

	for (const item of sortForDisplay(list)) {
		if (isNewRecord(item) || item.status !== MARKETING_CONTENT_STATUS.ACTIVE) {
			result.push(item)
			continue
		}

		result.push({ ...item, order: next })
		next += 1
	}

	return result
}

/**
 * One category's records, kept in local state and updated from what each write returns — the
 * API has no GET for this, by design.
 */
const MarketingContentSection = (props) => {
	const { category, facilityId, records, onChange, titleStyles = {} } = props
	const classes = useStyles()
	const dispatch = useDispatch()

	const config = CATEGORY_CONFIG[category]
	const [busy, setBusy] = useState(false)
	const [justAddedId, setJustAddedId] = useState(null)

	const sorted = sortForDisplay(records)

	let activeCount = 0
	let hasUnsaved = false
	for (const item of sorted) {
		if (item.status === MARKETING_CONTENT_STATUS.ACTIVE) {
			activeCount += 1
		}
		if (isNewRecord(item)) {
			hasUnsaved = true
		}
	}

	// Create-on-add: the record is persisted immediately as an inactive, artwork-less draft.
	// It stays a Draft until it has artwork and can go active. Tracked in justAddedId so its card opens expanded.
	const add = async () => {
		setBusy(true)
		const created = await dispatch(createMarketingContent(facilityId, { category }))
		setBusy(false)

		if (created) {
			setJustAddedId(created._id)
			onChange([...records, created])
		}
	}

	const replace = (saved, options = {}) => {
		let updated = []
		let matched = false

		for (const item of records) {
			if (item._id && item._id === saved._id) {
				updated.push(saved)
				matched = true
				continue
			}

			updated.push(item)
		}

		// A first-time save has no id in the list yet; with only one unsaved record allowed,
		// that placeholder is the one that just saved.
		if (!matched) {
			for (let i = 0; i < updated.length; i += 1) {
				if (isNewRecord(updated[i])) {
					updated[i] = saved
					break
				}
			}
		}

		// Single-active categories: activating one deactivates the rest server-side, so mirror
		// that here rather than refetch.
		if (!config.allowMultiple && saved.status === MARKETING_CONTENT_STATUS.ACTIVE) {
			updated = updated.map((item) =>
				item._id !== saved._id && item.status === MARKETING_CONTENT_STATUS.ACTIVE
					? { ...item, status: MARKETING_CONTENT_STATUS.INACTIVE, order: NO_ORDER }
					: item
			)
		}

		onChange(options.reordered ? renumber(updated) : updated)
	}

	const removeFromList = (removed) => {
		const remaining = []

		for (const item of records) {
			if (item._id !== removed._id) {
				remaining.push(item)
			}
		}

		onChange(renumber(remaining))
	}

	/**
	 * Reorder posts the whole category — the API rejects a partial list. Only active records
	 * carry an order, so inactive drafts are excluded.
	 */
	const move = async (from, to) => {
		const activeIds = []
		for (const item of sorted) {
			if (item.status === MARKETING_CONTENT_STATUS.ACTIVE) {
				activeIds.push(item._id)
			}
		}

		if (to < 0 || to >= activeIds.length) {
			return
		}

		const [moved] = activeIds.splice(from, 1)
		activeIds.splice(to, 0, moved)

		setBusy(true)
		const reordered = await dispatch(reorderMarketingContent(facilityId, category, activeIds))
		setBusy(false)

		if (!reordered) {
			return
		}

		// The response omits unsaved drafts (never sent), so re-append them.
		const drafts = []
		for (const item of records) {
			if (isNewRecord(item)) {
				drafts.push(item)
			}
		}

		onChange([...reordered, ...drafts])
	}

	return (
		<Box className={classes.wrapper}>
			<Box className={classes.header}>
				<Box className={classes.titleGroup}>
					<Typography className={classes.title} style={{ ...titleStyles }}>
						{config.title}
					</Typography>
					{config.allowMultiple ? null : (
						<StyledAlert
							type="info"
							text="Only 1 can be active at a time. Activating one will deactivate the others"
						/>
					)}
				</Box>
				<MainButton
					label="Add"
					startIcon={<AddOutlined />}
					onClick={add}
					// One unsaved record at a time, so replace() knows which placeholder just saved.
					disabled={busy || hasUnsaved}
				/>
			</Box>

			{sorted.length ? (
				sorted.map((record, index) => (
					<MarketingContentItem
						key={record._id || record._tempId || `new-${index}`}
						record={record}
						facilityId={facilityId}
						category={category}
						index={index}
						total={activeCount}
						disabled={busy}
						onSaved={replace}
						onRemoved={removeFromList}
						onMove={move}
						defaultExpanded={record._id === justAddedId}
					/>
				))
			) : (
				<Box className={classes.empty}>
					<Typography variant="body2">Nothing here yet.</Typography>
				</Box>
			)}
		</Box>
	)
}

export default MarketingContentSection
